import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { find, isEmpty, isObject, map, omit } from 'lodash';
import { Box, Grid, Paper, Tab, Tabs } from '@mui/material';
import { format } from 'date-fns';

import TimeLineView from 'common/modules/TimelineView';
import useTabContext, { TabProvider } from 'common/hooks/useTabContext';
import NothingFound from 'common/components/NothingFound';
import { loadRequestDetails } from 'store/thunks/orderStatusThunks';

const TabPanel = ({ children, value }) => {
  const { tab } = useTabContext();
  const hidden = value !== tab;
  return (
    <div
      role='tabpanel'
      hidden={hidden}
      id={`work-queue-tabpanel-${value}`}
      aria-labelledby={`work-queue-tab-${value}`}
    >
      <Box sx={{ px: 1 }}>{children}</Box>
    </div>
  );
};

TabPanel.propTypes = {
  children: PropTypes.node.isRequired,
  value: PropTypes.string.isRequired,
};

const a11yProps = (index) => ({
  id: `work-queue-tab-${index}`,
  'aria-controls': `work-queue-tabpanel-${index}`,
});

const EScriptTimelineView = ({ referenceId, requestId, timeLineType, mpi, orderId }) => {
  const dispatch = useDispatch();
  const { pages, currentPage } = useSelector(({ orderStatus: { eScriptOrders } }) => eScriptOrders);
  const patientOrders = useSelector(({ patient }) => patient[mpi]?.orders ?? []);
  const patientOrderHistory = useSelector(({ patient }) => patient[mpi]?.orderHistory ?? []);

  const orderData =
    find(pages[currentPage], { referenceId, requestId }) ||
    find(patientOrders, { orderId }) ||
    find(patientOrderHistory, { orderId });

  const partnerId = orderData?.partner?.partnerUuid || orderData?.partnerId;
  const isFromPatientProfile =
    !!find(patientOrders, { orderId }) || !!find(patientOrderHistory, { orderId });

  const handleResendSuccess = () => {
    if (!referenceId || !requestId || !partnerId) return;
    dispatch(
      loadRequestDetails({
        referenceId,
        requestId,
        orderId,
        partnerId,
        mpi,
        fromPatientProfile: isFromPatientProfile,
      })
    );
  };

  const timeLineData =
    orderData?.requestDetails?.[timeLineType] || orderData?.orderDetails?.[timeLineType];

  const timeLinePayload = map(
    timeLineData,
    ({
      id,
      actionItemType,
      timestamp,
      interactionData,
      isProcessSuccess,
      orderShipmentId,
      user,
    }) => {
      const payload = omit(interactionData, ['originalEventPayload']);
      const requestDetails = {
        firstName: orderData?.patient?.firstName,
        lastName: orderData?.patient?.lastName,
        mpi: orderData?.patient?.mpi,
        actionItemType,
        orderId: orderData?.pomOrderId,
        requestId: interactionData?.requestId,
        orderShipmentId,
        rxNumber: interactionData?.rxNumber,
        referenceId: orderData?.referenceId,
        user,
        fromPharmacyNpi: interactionData?.transferFrom?.npi,
        toPharmacyNpi: interactionData?.transferTo?.npi,
        originalRequestId:
          orderData?.requestDetails?.originalRequestId ||
          orderData?.orderDetails?.originalRequestId,
        eventRegistryPkId: interactionData?.eventRegistryPkId,
        isWebhookManualResend: interactionData?.isWebhookManualResend,
      };

      const isShipstationShipped = actionItemType === 'SHIPSTATION_ORDER_SHIPPED';
      const [shipYear, shipMonth, shipDay] = (interactionData?.shipDate || '').split('-');
      const shipstationShippedTimestamp = interactionData?.shipDate
        ? `${shipMonth}/${shipDay}/${shipYear}`
        : format(new Date(timestamp), 'MM/dd/yyyy');
      const formattedTimestamp = isShipstationShipped
        ? shipstationShippedTimestamp
        : format(new Date(timestamp), 'MM/dd/yyyy hh:mm:ss a');

      return {
        id,
        requestDetails,
        actionItemType,
        isProcessSuccess,
        eventPayload: { ...(isObject(payload) ? payload : { payload }) },
        timestamp: formattedTimestamp,
      };
    }
  );

  return (
    <Grid container sx={{ mt: 3 }}>
      {!isEmpty(timeLinePayload) ? (
        <TimeLineView
          timeLineData={timeLinePayload}
          mpi={mpi}
          onResendSuccess={handleResendSuccess}
        />
      ) : (
        <NothingFound />
      )}
    </Grid>
  );
};

EScriptTimelineView.propTypes = {
  requestId: PropTypes.string.isRequired,
  referenceId: PropTypes.string.isRequired,
  timeLineType: PropTypes.string.isRequired,
  mpi: PropTypes.string.isRequired,
  orderId: PropTypes.string.isRequired,
};

const tabsDetails = [
  {
    tabName: 'Events',
    Component: EScriptTimelineView,
    tabValue: 'events',
  },
  {
    tabName: 'Errors',
    Component: EScriptTimelineView,
    tabValue: 'errors',
  },
];

const TabList = () => {
  const { tab, setTab } = useTabContext();

  return (
    <Box sx={{ borderBottom: 2, borderColor: 'divider' }}>
      <Tabs
        value={tab}
        onChange={setTab}
        aria-label='Patient Details Tabs'
        scrollButtons='auto'
        sx={{ maxWidth: '85vw' }}
        variant='scrollable'
      >
        {map(tabsDetails, ({ tabName, tabValue }, key) => (
          <Tab label={tabName} {...a11yProps(key)} key={key} value={tabValue} />
        ))}
      </Tabs>
    </Box>
  );
};

const EScriptOrdersProgressTab = ({ initialTab, referenceId, requestId, mpi, orderId }) => {
  return (
    <TabProvider initialValue={initialTab}>
      <Grid container component={Paper} sx={{ px: 2 }} direction='column'>
        <TabList />
        {map(tabsDetails, ({ Component, tabValue }) => (
          <TabPanel value={tabValue} key={tabValue}>
            <Component
              referenceId={referenceId}
              requestId={requestId}
              timeLineType={tabValue}
              mpi={mpi}
              orderId={orderId}
            />
          </TabPanel>
        ))}
      </Grid>
    </TabProvider>
  );
};

EScriptOrdersProgressTab.propTypes = {
  initialTab: PropTypes.string.isRequired,
  requestId: PropTypes.string.isRequired,
  referenceId: PropTypes.string.isRequired,
  mpi: PropTypes.string.isRequired,
  orderId: PropTypes.string.isRequired,
};

export default EScriptOrdersProgressTab;
